import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { convertEpochToDateString } from '@functions/functions';
import { GlobalFunctions, imagePath, imgTypes } from '@global/GlobalFunctions';
import { IQueryParameterFiltering, QueryParamType } from '@global/iFiltering';
import { IAccount } from '@services/accounts.service';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { Organisation, OrganisationsHierarchy } from '@services/organisation-data.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { DropDownList } from '@syncfusion/ej2-angular-dropdowns';
import { combineLatest, distinctUntilChanged, filter, finalize, firstValueFrom, Subject, take, takeUntil } from 'rxjs';


/**
 * FilterQueryParams
 *
 * Used to filter the table of Rules
 */
export interface FilterQueryParams {

	Location?: string;

	Filter?: string;
	pageIndex?: number;
	pageSize?: number;

}


@Component({
	selector: 'app-organisation-flagged-assets',
	templateUrl: './organisation-flagged-assets.component.html',
	styleUrls: ['./organisation-flagged-assets.component.scss']
})
export class OrganisationFlaggedAssetsComponent implements OnInit, IQueryParameterFiltering<FilterQueryParams> {


	// =========================================================================
	// Imports / interfaces
	// =========================================================================

	// Interfaces are declared above the component.


	// =========================================================================
	// Component state
	// =========================================================================

	/** Observable Subject Finalise */
	private destroyRef = inject(DestroyRef);

	/** Initialised filterQueryParams */
	filterQueryParams: FilterQueryParams = {};

	/** Watch Status */
	public FavouriteAssets: any[] = [];

	public AccountGUID: string;

	public OrganisationUID: string;

	public currentUser: WebAppUser;

	/** selectedAccount */
	public selectedAccount: IAccount;

	/** selectedOrganisation */
	public selectedOrganisation: Organisation;


	// =========================================================================
	// Query/filter configuration
	// =========================================================================

	/**
	 * filterQueryParamTypes
	 *
	 * Definition of the Query Parameter data types
	 */
	filterQueryParamTypes: Record<keyof FilterQueryParams, QueryParamType> = {

		Location: 'string',

		Filter: 'string',
		pageIndex: 'number',
		pageSize: 'number'

	};


	// =========================================================================
	// Table/data-source configuration
	// =========================================================================

	/** assetTableColumns */
	assetTableColumns: string[] = [
		'Image',
		'Name',
		'AddedByName',
		'Comment',
		'HierarchyPath',
		'AddedAt',
		'GoToAsset',
		'RemoveAsset'
	];

	/** datasource */
	dataSourceAssets: MatTableDataSource<any>;

	/**
	 * assignAssetsToDataSource
	 */
	assignAssetsToDataSource() {

		if (!this.dataSourceAssets) {
			this.dataSourceAssets = new MatTableDataSource(this.FavouriteAssets);
		}
		else {
			this.dataSourceAssets.data = this.FavouriteAssets;
		}

		this.dataSourceAssets.filterPredicate = (asset: any, filter: string) => {

			if (!this.flattenedAssetIDs.includes(asset.AssetID)) {
				return false;
			}

			const query = filter?.toLowerCase().trim() ?? '';
			if (!query) { return true; }

			// Combine the necessary fields for filtering
			const combinedStr = [
				asset.Name,
				asset.AddedByName,
				asset.Comment
			]
				.filter(value => value != null)
				.join(' ')
				.toLowerCase();

			return combinedStr.includes(query)
		};

		this.dataSourceAssets.sortingDataAccessor = (item, property) => {
			switch (property) {
				case 'Name': return item.Name || '';
				case 'AddedByName': return item.AddedByName || '';
				case 'Comment': return item.Comment || '';
				case 'HierarchyPath': return item.HierarchyPath || '';
				case 'AddedAt': return item.AddedAt || '';
				default: return '';
			}
		};

	}


	// =========================================================================
	// Paginator and sort configuration
	// =========================================================================

	private _paginatorAssets: MatPaginator;

	@ViewChild('paginatorAssets')
	set paginatorAssets(paginator: MatPaginator) {
		try {
			if (!paginator) { return; }

			if (this.filterQueryParams.pageSize) { paginator.pageSize = this.filterQueryParams.pageSize; }
			if (this.filterQueryParams.pageIndex) { paginator.pageIndex = this.filterQueryParams.pageIndex; }

			this._paginatorAssets = paginator;

			if (this.dataSourceAssets) {
				this.dataSourceAssets.paginator = paginator;
			}
		} catch (error) {
			console.error('Error assigning Asset paginator:', error);
		}
	}

	private _sortAssets: MatSort;

	@ViewChild('sortAssets')
	set sortAssets(sort: MatSort) {
		try {
			if (!sort) { return; }

			this._sortAssets = sort;

			if (this.dataSourceAssets) {
				this.dataSourceAssets.sort = sort;
			}
		} catch (error) {
			console.error('Error assigning Rules sort:', error);
		}
	}


	// =========================================================================
	// Location / hierarchy configuration
	// =========================================================================

	public hierarchy: OrganisationsHierarchy[];

	/** hierarchyBreadcrumbs */
	private hierarchyBreadcrumbs: Map<number, string>;

	public selectedLocation?: OrganisationsHierarchy;

	/** flattenedAssetIDs */
	flattenedAssetIDs: number[] = [];

	/** location Assets array */
	locationAssets: LevelAsset[] = [];

	public dropdownLocations: OrganisationsHierarchy[] = [];

	/** locationFields */
	public dropdownLocationFields: { text: string, value: string } = { text: "Name", value: "Uid" };

	private _locationDropdown: DropDownList;

	/** locationDropdown */
	@ViewChild('locationDropdown')
	set locationDropdown(dropdown: DropDownList) {
		try {
			if (!dropdown) { return; }

			this._locationDropdown = dropdown;
			dropdown.showClearButton = true;
		} catch (error) {
			console.error('Error assigning location Dropdown:', error);
		}
	}


	// =========================================================================
	// Syncfusion grid configuration
	// =========================================================================

	public fields: { text: string, value: string } = { text: 'Name', value: 'AssetID' };


	// =========================================================================
	// Constructor
	// =========================================================================

	constructor(
		public assetsDataService: AssetsDataService,
		public globalFunctions: GlobalFunctions,
		public authenticationService: AuthenticationService,
		private siteSubscriptions: siteSubscriptions,
		private router: Router,
		private route: ActivatedRoute,
	) {

	}


	// =========================================================================
	// Lifecycle
	// =========================================================================

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
		this.globalFunctions.displaySpinner(true);

		combineLatest([
			this.siteSubscriptions.hierarchicalData$.pipe(distinctUntilChanged()),
			this.authenticationService.currentUser$.pipe(distinctUntilChanged()),
			this.route.queryParamMap.pipe(take(1))
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([hierarchicalData, currentUser, queryParamMap]) =>
				!!hierarchicalData && !!currentUser && !!queryParamMap),
			takeUntilDestroyed(this.destroyRef)
		).subscribe({
			next: async ([hierarchicalData, currentUser, queryParamMap]) => {

				let [selectedAccount, selectedAccountGUID, selectedOrganisation, selectedOrganisationUID] = await firstValueFrom(
					combineLatest([
						this.siteSubscriptions.selectedAccount$.pipe(take(1)),
						this.siteSubscriptions.selectedAccountGUID$.pipe(take(1)),
						this.siteSubscriptions.selectedOrganisation$.pipe(take(1)),
						this.siteSubscriptions.selectedOrganisationUID$.pipe(take(1))
					]
					)
				);

				this.selectedAccount = selectedAccount;
				this.AccountGUID = selectedAccountGUID;
				this.selectedOrganisation = selectedOrganisation;
				this.OrganisationUID = selectedOrganisationUID;

				let urlAccountID = this.router.url.split('/')[1];

				if (this.AccountGUID.toLowerCase() != urlAccountID.toLowerCase() && this.router.url.includes('flagged') && !this.router.url.includes('routing#')) {
					this.router.navigateByUrl(`routing#${this.router.url}`);
				}

				this.currentUser = currentUser;
				this.hierarchy = JSON.parse(hierarchicalData);
				this.hierarchyBreadcrumbs = this.flattenAssetBreadcrumbs(this.hierarchy);

				/** Default to look for only open cases */
				this.filterQueryParams = this.globalFunctions.queryParamsToObject<FilterQueryParams>(
					queryParamMap,
					this.filterQueryParamTypes
				);

				this.dropdownLocations = [];
				if (this.hierarchy[0].SubChild) {
					this.hierarchy[0].SubChild.forEach(subchild => {
						this.dropdownLocations.push(subchild);
					});
				}

				this.selectedLocation = this.dropdownLocations.find(
					subchild => subchild.Uid == this.filterQueryParams.Location
				) ?? this.hierarchy[0];


				if (this.selectedLocation) {
					this.locationAssets = this.flattenAssets([this.selectedLocation]);
					this.flattenedAssetIDs = this.locationAssets.map(asset => asset.AssetID);
				}

				await this.getFlaggedAssets();

				this.applyQueryParameters("All", false);

				this.globalFunctions.displaySpinner(false);
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
				this.globalFunctions.displaySpinner(false);
			}
		});

	}


	// =========================================================================
	// Event loading
	// =========================================================================

	/**
	 * getFlaggedAssets
	 */
	async getFlaggedAssets() {

		try {
			const result = await firstValueFrom(
				this.assetsDataService.getFlaggedAssets$(this.AccountGUID, this.OrganisationUID)
					.pipe(
						filter((result) => !!result)
					)
			);

			if (result) {
				if (result[0].Data) {
					this.FavouriteAssets = JSON.parse(result[0].Data);
					this.FavouriteAssets = this.FavouriteAssets
						.map((x: LevelAsset) => {
							x.HierarchyPath = this.hierarchyBreadcrumbs.get(x.AssetID) ?? '';
							return x;
						});
				} else {
					this.FavouriteAssets = []
				}
			}
		}
		catch (error: any) {
			if (error.status != 404) {
				this.globalFunctions.HandleError(error, "this.assetsDataService.getFlaggedAssets$");
			}
		} finally {
			this.assignAssetsToDataSource();
		}
	}


	// =========================================================================
	// Event table/filter configuration
	// =========================================================================

	/**
	 * onPageChange
	 */
	onPageChange(pag: MatPaginator, event: PageEvent): void {

		if (pag == this._paginatorAssets) {
			this.filterQueryParams.pageSize = pag.pageSize;
			this.filterQueryParams.pageIndex = pag.pageIndex;
		}

		this.applyQueryParameters("All", false)
	}


	// =========================================================================
	// Filtering / query parameters
	// =========================================================================

	/**
	 * Applies the current query parameters to the component's data.
	 *
	 * @param page - The page or data set being queried.
	 * @param reQuery - Determines whether the data should be queried again.
	 */
	applyQueryParameters(page: string = "All", reQuery: boolean = false) {

		if (this.dataSourceAssets) {
			// Force MatTableDataSource to re-run filterPredicate
			const currentFilter = this.dataSourceAssets.filter;
			this.dataSourceAssets.filter = '';
			this.dataSourceAssets.filter = currentFilter || this.filterQueryParams.Filter || ' ';
		}

		this.rebuildURL(reQuery);
	}

	/**
	 * Rebuilds the URL using the current query parameter values.
	 *
	 * @param reQuery - Determines whether the data should be queried again
	 * after the URL has been rebuilt.
	 */
	rebuildURL(reQuery: boolean): void {
		if (reQuery) {
			this.router.navigate([], { queryParams: this.filterQueryParams });
		}
		else {
			const urlTree = this.router.createUrlTree([], {
				queryParams: this.filterQueryParams,
				queryParamsHandling: 'merge'
			});

			window.history.replaceState(
				window.history.state,
				'',
				this.router.serializeUrl(urlTree)
			);
		}
	}

	/**
	 * Handles a change to a query parameter.
	 *
	 * @param changed - The name of the query parameter that has changed.
	 */
	onQueryParameterChange(changed: keyof typeof this.filterQueryParams, event: any) {

		if (changed == "Filter") {
			const filterValue = (event.target as HTMLInputElement).value;
			this.dataSourceAssets.filter = filterValue.trim().toLowerCase();
		}

		if (changed == "Location") {

			if (this.locationDropdown) {
				this.locationDropdown.showClearButton = true;
			}

			if (event.itemData) {
				this.selectedLocation = <OrganisationsHierarchy>event.itemData;
				this.filterQueryParams.Location = event.value;
			}
			else if (!event.itemData) {
				this.selectedLocation = this.hierarchy[0];
				this.filterQueryParams.Location = undefined;
			}

			if (this.selectedLocation) {
				this.locationAssets = this.flattenAssets([this.selectedLocation]);
				this.flattenedAssetIDs = this.locationAssets.map(asset => asset.AssetID);
			}

		}

		this.applyQueryParameters("All", false);
	}


	// =========================================================================
	// Date handling
	// =========================================================================

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}


	// =========================================================================
	// Navigation / selection
	// =========================================================================

	/**
	 * goToAsset
	 */
	goToAsset(asset: LevelAsset) {
		this.siteSubscriptions.SetSelectedAsset(asset);
		this.router.navigate([`./${this.AccountGUID}/organisation/${this.OrganisationUID}/location/${asset.ParentUID}/asset/${asset.AssetID}`]);
	}


	// =========================================================================
	// 15. Asset / hierarchy helpers
	// =========================================================================

	/** Getting list of assets from an input hierarchy */
	flattenAssets(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.Assets && item.Assets.length > 0) {
				result = result.concat(item.Assets);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenAssets(item.SubChild));
		}

		return result;
	}

	/**
	 * Returns a map of AssetID -> Hierarchy breadcrumb
	 */
	flattenAssetBreadcrumbs(
		hierarchy: OrganisationsHierarchy[],
		path: string[] = [],
		result = new Map<number, string>()
	): Map<number, string> {

		if (!Array.isArray(hierarchy)) {
			return result;
		}

		for (const item of hierarchy) {

			const currentPath = [...path, item.Name];

			// Store breadcrumb for every asset at this level
			for (const asset of item.Assets ?? []) {
				result.set(asset.AssetID, currentPath.join(' > '));
			}

			// Recurse into children
			this.flattenAssetBreadcrumbs(
				item.SubChild,
				currentPath,
				result
			);
		}

		return result;
	}


	// =========================================================================
	// Model helpers
	// =========================================================================

	// No model helper methods currently defined.


	// =========================================================================
	// Miscellaneous display helpers
	// =========================================================================

	/** Path to Assets Images */
	public imagePathAssets(): string {
		return imagePath(this.AccountGUID, imgTypes.organisationassets).toLowerCase()
	}

	/**
	 * Edit the Location details
	 * @param imageURL
	 */
	viewImage(imageURL: string): void {
		this.globalFunctions.viewImageFullSize(imageURL);
	}

	/**
	 * removeFavourite
	 * @param asset asset
	 */
	removeFavourite(asset: LevelAsset) {
		this.globalFunctions.UserConfirm$("Remove " + asset.Name + " - " + asset.Description + " as a flagged asset?").subscribe(async OKResult => {
			if (OKResult) {
				// Create a local Subject to control the subscription lifecycle
				const fetchDataSubject = new Subject<void>();

				this.assetsDataService.deleteFlaggedAsset$(this.AccountGUID, this.OrganisationUID, asset.AssetID).pipe(
					takeUntil(fetchDataSubject), distinctUntilChanged(),
					filter((response) => !!response),
					finalize(() => {
						// Ensure the subscription is terminated and the subject is completed
						fetchDataSubject.next();
						fetchDataSubject.complete();
					})
				).subscribe({
					next: (result) => {
						this.getFlaggedAssets();
						this.globalFunctions.DisplaySnackBar(
							{
								message: "Flagged Asset Removed",
								duration: 1000
							}
						);
					},
					error: (error) => { this.globalFunctions.HandleError(error, "Error: this.assetsDataService.deleteFlaggedAsset$", false); },
				});
			}
		});
	}

}